import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CLModal from '../../../../components/CLModal';
import CLButton from '../../../../components/CLButton';
import CLField from '../../../../components/CLField';
import CLInput from '../../../../components/CLInput';

import Icon from './icon';
import { SubHeader, SuccessContainer } from './styled';
import { httpApplyForProperty } from '../../../../services/http';

const initialForm = {
  first_name: '',
  last_name: '',
  email: '',
};

const DialogApply = ({
  websiteId,
  listing,
  isVisible,
  onClose,
}) => {
  const { t } = useTranslation();

  const [form, updateForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setSubmitted(false);
      setErrors({});
      updateForm(initialForm);
    }
  }, [isVisible]);

  const onSubmit = async () => {
    setLoading(true);

    try {
      const response = await httpApplyForProperty({
        website_id: websiteId,
        listing_id: listing.listing_id,
        params: {
          property: listing.listing_property.property_id,
          ...form,
        },
      });

      setLoading(false);

      if (response.status === 201) {
        setSubmitted(true);
      }
    } catch (e) {
      setErrors(e.response.data);
      setLoading(false);
    }
  };

  const onChangeForm = (value, fieldCode) => {
    updateForm((prev) => ({
      ...prev,
      [fieldCode]: value,
    }));

    setErrors((errors) => {
      const newErrors = { ...errors };
      delete newErrors[fieldCode];

      return newErrors;
    });
  };

  const renderContent = () => {
    if (submitted) {
      return (
        <>
          <SubHeader>{listing.listing_property.display_name}</SubHeader>
          <SuccessContainer>
            <Icon />
            <h3>{t('message_sent', 'Message Sent')}</h3>
            <p>{t('get_back_soon', 'We will get back to you as soon as possible')}</p>
          </SuccessContainer>
        </>
      );
    }

    return (
      <>
        <SubHeader>{listing.listing_property.display_name}</SubHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <CLField
            label={t('first_name', 'First Name')}
            error={errors.first_name}
            field={() => <CLInput value={form.first_name} onChange={(value) => onChangeForm(value, 'first_name')} />}
          />

          <CLField
            label={t('last_name', 'Last Name')}
            error={errors.last_name}
            field={() => <CLInput value={form.last_name} onChange={(value) => onChangeForm(value, 'last_name')} />}
          />

          <CLField
            label={t('email', 'Email')}
            error={errors.email}
            field={() => <CLInput value={form.email} onChange={(value) => onChangeForm(value, 'email')} />}
          />

          <input style={{ display: 'none' }} type="submit" />
        </form>
      </>
    );
  };

  const renderFooter = () => {
    if (submitted) {
      return (
        <>
          <CLButton primary onClick={onClose}>{t('close', 'Close')}</CLButton>
        </>
      );
    }

    return (
      <>
        <CLButton onClick={onClose}>{t('cancel', 'Cancel')}</CLButton>
        <CLButton
          primary
          type="submit"
          onClick={onSubmit}
          loading={loading}
        >
          {t('apply', 'Apply')}
        </CLButton>
      </>
    );
  };

  return (
    <CLModal
      isVisible={isVisible}
      onClose={onClose}
      title={t('apply_for_property', 'Apply for Property')}
      renderContent={renderContent}
      renderFooter={renderFooter}
    />
  );
};

DialogApply.propTypes = {
  websiteId: PropTypes.string.isRequired,
  listing: PropTypes.object.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DialogApply;
